'use client';

import { JSX, useMemo } from 'react';

// Components
import { AtClickable, AtHeader, AtText } from '../../atoms';

// Types
import {
  EAlignment,
  EOrientation,
  EMlHeaderSize,
  IMlHeaderProps,
} from './ml-header.types';

// Hooks + Utils
import useBreakpoint from '@/hooks/useBreakpoint';
import mlHeaderVariants from './ml-header.variants';
import classNames from 'classnames';

/**
 * Renders a styled marketing header with a heading, optional body copy, and optional CTA.
 *
 * **/
export const MlHeader = ({
  head,
  body,
  ctaProps,
  alignment = EAlignment.LEFT,
  order = EOrientation.VERTICAL,
  className,
  size = EMlHeaderSize.LARGE,
  headElement,
}: IMlHeaderProps): JSX.Element => {
  const bp = useBreakpoint();

  const { textContainer, base, cta } = mlHeaderVariants({
    alignment: alignment as 'left' | 'center' | undefined,
    order,
    size,
  });

  const headerSize = useMemo(() => {
    if (size === EMlHeaderSize.LARGE) return 'h1';
    if (size === EMlHeaderSize.SMALL) return 'h2';
  }, [size]);

  const isDesktop = ['lg', 'xl', '2xl', '3xl'].includes(bp);
  const shouldNotRenderCTA =
    (isDesktop && size === EMlHeaderSize.LARGE) ||
    (isDesktop && alignment === EAlignment.CENTER);

  return (
    <div className={classNames(base(), className)} data-testid="ml-header">
      <div className={textContainer()} data-testid="ml-header-text-container">
        <AtHeader
          element={headElement || headerSize}
          variant={headerSize}
          className="font-Thermal"
        >
          {head}
        </AtHeader>
        {body && (
          <AtText
            type="p"
            className="font-Figtree text-skin-tertiary"
            data-testid="ml-header-body"
          >
            {body}
          </AtText>
        )}
      </div>
      {/* We don't show the CTA on desktop when alignment is center https://schumacherhomes.atlassian.net/browse/WR-354 */}
      {/* We don't show the CTA on desktop when size is large https://schumacherhomes.atlassian.net/browse/WR-357 */}
      {ctaProps && shouldNotRenderCTA === false && (
        <AtClickable
          data-testid="ml-header-cta"
          className={cta()}
          {...ctaProps}
          interactLocation="ml-header"
        />
      )}
    </div>
  );
};
